import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Search, MapPin, Monitor, Zap, Briefcase, ArrowLeft, ChevronLeft, ChevronRight, Filter, ExternalLink, Globe, Clock, Star, CheckCircle2, DollarSign, Shield, EyeOff, AlertCircle, Building2 } from 'lucide-react';
import { formatPayDisplay } from '../utils/payDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'customer-service', label: 'Customer Service' },
  { value: 'chat-support', label: 'Chat Support' },
  { value: 'data-entry', label: 'Data Entry' },
  { value: 'admin', label: 'Admin & VA' },
  { value: 'sales', label: 'Sales' },
  { value: 'writing', label: 'Writing' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'tech-support', label: 'Tech Support' },
  { value: 'tech', label: 'Tech & Dev' },
  { value: 'accounting', label: 'Accounting' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'design', label: 'Design' },
  { value: 'federal', label: 'Federal/Government' },
];

const STATES = [
  { value: 'all', label: 'All States' },
  { value: 'Nationwide', label: 'Nationwide' },
  { value: 'Washington DC', label: 'Washington DC' },
  { value: 'Alabama', label: 'Alabama' },{ value: 'Alaska', label: 'Alaska' },{ value: 'Arizona', label: 'Arizona' },
  { value: 'Arkansas', label: 'Arkansas' },{ value: 'California', label: 'California' },{ value: 'Colorado', label: 'Colorado' },
  { value: 'Connecticut', label: 'Connecticut' },{ value: 'Delaware', label: 'Delaware' },{ value: 'Florida', label: 'Florida' },
  { value: 'Georgia', label: 'Georgia' },{ value: 'Hawaii', label: 'Hawaii' },{ value: 'Idaho', label: 'Idaho' },
  { value: 'Illinois', label: 'Illinois' },{ value: 'Indiana', label: 'Indiana' },{ value: 'Iowa', label: 'Iowa' },
  { value: 'Kansas', label: 'Kansas' },{ value: 'Kentucky', label: 'Kentucky' },{ value: 'Louisiana', label: 'Louisiana' },
  { value: 'Maine', label: 'Maine' },{ value: 'Maryland', label: 'Maryland' },{ value: 'Massachusetts', label: 'Massachusetts' },
  { value: 'Michigan', label: 'Michigan' },{ value: 'Minnesota', label: 'Minnesota' },{ value: 'Mississippi', label: 'Mississippi' },
  { value: 'Missouri', label: 'Missouri' },{ value: 'Montana', label: 'Montana' },{ value: 'Nebraska', label: 'Nebraska' },
  { value: 'Nevada', label: 'Nevada' },{ value: 'New Hampshire', label: 'New Hampshire' },{ value: 'New Jersey', label: 'New Jersey' },
  { value: 'New Mexico', label: 'New Mexico' },{ value: 'New York', label: 'New York' },{ value: 'North Carolina', label: 'North Carolina' },
  { value: 'North Dakota', label: 'North Dakota' },{ value: 'Ohio', label: 'Ohio' },{ value: 'Oklahoma', label: 'Oklahoma' },
  { value: 'Oregon', label: 'Oregon' },{ value: 'Pennsylvania', label: 'Pennsylvania' },{ value: 'Rhode Island', label: 'Rhode Island' },
  { value: 'South Carolina', label: 'South Carolina' },{ value: 'South Dakota', label: 'South Dakota' },{ value: 'Tennessee', label: 'Tennessee' },
  { value: 'Texas', label: 'Texas' },{ value: 'Utah', label: 'Utah' },{ value: 'Vermont', label: 'Vermont' },
  { value: 'Virginia', label: 'Virginia' },{ value: 'Washington', label: 'Washington' },{ value: 'West Virginia', label: 'West Virginia' },
  { value: 'Wisconsin', label: 'Wisconsin' },{ value: 'Wyoming', label: 'Wyoming' },
];

export default function JobSearch() {
  const urlParams = new URLSearchParams(window.location.search);
  const initialSearch = urlParams.get('search') || '';

  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState(initialSearch);
  const [category, setCategory] = useState('all');
  const [state, setState] = useState('all');
  const [equipmentProvided, setEquipmentProvided] = useState('all');
  const [quickApplyOnly, setQuickApplyOnly] = useState('all');
  const [loading, setLoading] = useState(true);
  const [posted, setPosted] = useState('all');
  const [jobType, setJobType] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [showFilters, setShowFilters] = useState(false);
  const [sections, setSections] = useState(null);
  const [platformContext, setPlatformContext] = useState(null);
  const navigate = useNavigate();

  // Look up platform context if search matches a WFH platform
  useEffect(() => {
    if (initialSearch) {
      axios.get(`${API}/platforms`).then(r => {
        const match = r.data.platforms.find(p => p.name.toLowerCase() === initialSearch.toLowerCase());
        if (match) setPlatformContext(match);
      }).catch(() => {});
    }
  }, [initialSearch]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 12 });
      if (search) params.append('search', search);
      if (category !== 'all') params.append('category', category);
      if (state !== 'all') params.append('state', state);
      if (equipmentProvided !== 'all') params.append('equipment_provided', equipmentProvided === 'yes');
      if (quickApplyOnly !== 'all') params.append('quick_apply', quickApplyOnly === 'yes');
      if (posted !== 'all') params.append('posted', posted);
      if (jobType !== 'all') params.append('job_type', jobType);
      if (sortBy === 'score') params.append('sort_by', 'score');
      const { data } = await axios.get(`${API}/jobs?${params}`);
      setJobs(data.jobs);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchJobs(); }, [page, category, state, equipmentProvided, quickApplyOnly, posted, jobType, sortBy]);
  useEffect(() => { axios.get(`${API}/jobs/sections`).then(r => setSections(r.data)).catch(() => {}); }, []);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); fetchJobs(); };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Nav */}
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center">
              <Briefcase size={14} className="text-white" strokeWidth={1.5} />
            </div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-sm text-[#1C2A24]/70 hover:text-[#047857]">Dashboard</Link>
            <Link to="/applications" className="text-sm text-[#1C2A24]/70 hover:text-[#047857]">My Apps</Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4 hover:opacity-80"><ArrowLeft size={14} strokeWidth={1.5} /> Back to Dashboard</Link>
          <h1 className="text-3xl md:text-4xl tracking-tight font-medium" style={{fontFamily:'Outfit'}}>Find WFH Jobs</h1>
          <p className="text-[#1C2A24]/60 mt-2">{total} remote jobs available</p>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="relative">
            <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#1C2A24]/40" strokeWidth={1.5} />
            <Input data-testid="job-search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search jobs, companies, keywords..."
              className="pl-12 h-14 rounded-full bg-white border-[#E5E0D8] text-base shadow-[0_8px_30px_rgb(0,0,0,0.04)]" />
            <Button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-10 px-6" data-testid="search-btn">Search</Button>
          </div>
        </form>

        {/* Quick Section Tabs */}
        {sections && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="section-tabs">
            {[
              {key: 'all', label: 'All Jobs', count: total},
              {key: 'hiring_now', label: 'Hiring Now', count: sections.hiring_now?.length || 0},
              {key: 'entry_level', label: 'Entry Level', count: sections.entry_level?.length || 0},
              {key: 'customer_service', label: 'Customer Service', count: sections.customer_service?.length || 0},
              {key: 'direct_apply', label: 'Direct Apply', count: sections.direct_apply?.length || 0},
            ].map(s => (
              <button key={s.key} onClick={() => { if (s.key === 'all') { setCategory('all'); setPosted('all'); } else if (s.key === 'hiring_now') { setCategory('all'); setPosted('week'); } else if (s.key === 'entry_level') { setCategory('customer-service'); } else if (s.key === 'customer_service') { setCategory('customer-service'); } else if (s.key === 'direct_apply') { setSortBy('score'); } setPage(1); }}
                className="px-4 py-2 rounded-full text-xs font-medium bg-white border border-[#E5E0D8] hover:border-[#047857]/30 transition-all" data-testid={`section-${s.key}`}>
                {s.label} <span className="text-[#1C2A24]/30 ml-1">{s.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Trusted Employers Bar */}
        {sections?.trusted_employer_names?.length > 0 && (
          <div className="flex items-center gap-3 mb-6 overflow-x-auto pb-2" data-testid="trusted-employers-bar">
            <span className="text-xs text-[#1C2A24]/40 shrink-0 flex items-center gap-1"><Shield size={12} className="text-[#047857]" /> Trusted:</span>
            {sections.trusted_employer_names.map(name => (
              <button key={name} onClick={() => { setSearch(name); setPage(1); fetchJobs(); }}
                className="shrink-0 px-3 py-1.5 rounded-full text-[11px] font-medium bg-[#A7F3D0]/20 text-[#047857] border border-[#047857]/10 hover:bg-[#A7F3D0]/40 transition-all">
                {name}
              </button>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4" data-testid="job-filters">
          <Select value={category} onValueChange={v => { setCategory(v); setPage(1); }}>
            <SelectTrigger className="w-[180px] rounded-full bg-white border-[#E5E0D8]" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>

          <Select value={posted} onValueChange={v => { setPosted(v); setPage(1); }}>
            <SelectTrigger className="w-[160px] rounded-full bg-white border-[#E5E0D8]" data-testid="filter-posted"><SelectValue placeholder="Posted" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any Time</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="3days">Last 3 Days</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={v => { setSortBy(v); setPage(1); }}>
            <SelectTrigger className="w-[160px] rounded-full bg-white border-[#E5E0D8]" data-testid="filter-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="date">Newest First</SelectItem>
              <SelectItem value="score">Best Match</SelectItem>
            </SelectContent>
          </Select>

          <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-1 px-4 py-2 rounded-full bg-white border border-[#E5E0D8] text-xs hover:border-[#047857]/30" data-testid="more-filters-btn">
            <Filter size={12} strokeWidth={1.5} /> More Filters
          </button>
        </div>

        {showFilters && (
          <div className="flex flex-wrap gap-3 mb-6 p-4 bg-white rounded-2xl border border-[#E5E0D8]" data-testid="expanded-filters">
            <Select value={state} onValueChange={v => { setState(v); setPage(1); }}>
              <SelectTrigger className="w-[180px] rounded-full bg-[#F4F1E8]/50 border-[#E5E0D8]" data-testid="filter-state"><SelectValue placeholder="State" /></SelectTrigger>
              <SelectContent>{STATES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>

            <Select value={equipmentProvided} onValueChange={v => { setEquipmentProvided(v); setPage(1); }}>
              <SelectTrigger className="w-[180px] rounded-full bg-[#F4F1E8]/50 border-[#E5E0D8]" data-testid="filter-equipment"><SelectValue placeholder="Equipment" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Equipment</SelectItem>
                <SelectItem value="yes">Equipment Provided</SelectItem>
                <SelectItem value="no">Own Equipment</SelectItem>
              </SelectContent>
            </Select>

            <Select value={quickApplyOnly} onValueChange={v => { setQuickApplyOnly(v); setPage(1); }}>
              <SelectTrigger className="w-[160px] rounded-full bg-[#F4F1E8]/50 border-[#E5E0D8]" data-testid="filter-quickapply"><SelectValue placeholder="Apply Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Jobs</SelectItem>
                <SelectItem value="yes">Quick Apply Only</SelectItem>
              </SelectContent>
            </Select>

            <Select value={jobType} onValueChange={v => { setJobType(v); setPage(1); }}>
              <SelectTrigger className="w-[160px] rounded-full bg-[#F4F1E8]/50 border-[#E5E0D8]" data-testid="filter-jobtype"><SelectValue placeholder="Job Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="full-time">Full-Time</SelectItem>
                <SelectItem value="part-time">Part-Time</SelectItem>
                <SelectItem value="contract">Contract</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* WFH Platforms Banner */}
        <div className="bg-[#A7F3D0]/20 rounded-2xl p-4 mb-6 flex items-center justify-between border border-[#047857]/10" data-testid="platforms-banner">
          <div className="flex items-center gap-3">
            <Globe size={18} className="text-[#047857]" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-medium text-[#1C2A24]" style={{fontFamily:'Outfit'}}>Explore 15+ WFH-Specific Platforms</p>
              <p className="text-xs text-[#1C2A24]/50">Arise, Omni Interactions, FlexJobs, LiveOps, and more</p>
            </div>
          </div>
          <Link to="/platforms">
            <Button size="sm" className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white text-xs" data-testid="browse-platforms-btn">
              Browse Platforms <ExternalLink size={12} className="ml-1" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>

        {/* Job Cards */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => <div key={i} className="bg-white rounded-3xl border border-[#E5E0D8] p-6 h-48 animate-pulse" />)}
          </div>
        ) : jobs.length === 0 ? (
          platformContext ? (
            /* Platform/employer-specific empty state */
            <div className="text-center py-16 max-w-lg mx-auto" data-testid="platform-empty-state">
              <div className="w-16 h-16 rounded-2xl bg-[#F4F1E8] flex items-center justify-center mx-auto mb-5">
                <Building2 size={28} className="text-[#1C2A24]/30" strokeWidth={1.5} />
              </div>
              <h2 className="text-xl font-medium mb-1" style={{fontFamily:'Outfit'}}>{platformContext.name}</h2>
              <span className={`inline-block text-[11px] px-2.5 py-0.5 rounded-full font-medium mb-4 ${
                platformContext.employer_type === 'staffing_agency' ? 'bg-amber-100 text-amber-700' :
                platformContext.employer_type === 'platform_contractor' ? 'bg-[#D97757]/10 text-[#D97757]' :
                platformContext.employer_type === 'job_board' ? 'bg-blue-50 text-blue-600' :
                'bg-[#047857]/10 text-[#047857]'
              }`}>{platformContext.employer_type_label}</span>
              <p className="text-base text-[#1C2A24]/60 mb-2">No live openings synced right now</p>
              <p className="text-sm text-[#1C2A24]/40 mb-6">Check back soon for new opportunities</p>
              {platformContext.employer_note && (
                <p className="text-xs text-amber-700/80 bg-amber-50 rounded-lg px-4 py-2 mb-6 inline-block">{platformContext.employer_note}</p>
              )}
              <div className="flex items-center justify-center gap-3">
                <a href={platformContext.url} target="_blank" rel="noopener noreferrer">
                  <Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-11 px-6" data-testid="browse-official-site">
                    Browse official site <ExternalLink size={14} className="ml-2" strokeWidth={1.5} />
                  </Button>
                </a>
                <Button variant="outline" className="rounded-full h-11 px-6 border-[#E5E0D8]" onClick={() => { setSearch(''); setPlatformContext(null); navigate('/jobs'); fetchJobs(); }} data-testid="clear-platform-search">
                  View all jobs
                </Button>
              </div>
            </div>
          ) : (
            /* General search empty state */
            <div className="text-center py-20" data-testid="general-empty-state">
              <Search size={48} className="text-[#E5E0D8] mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-lg text-[#1C2A24]/60">No jobs found matching your filters.</p>
            </div>
          )
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="job-results">
            {jobs.map(job => {
              const pay = formatPayDisplay(job.salary_range);
              const daysAgo = job.created_at ? Math.floor((Date.now() - new Date(job.created_at).getTime()) / 86400000) : null;
              const postedLabel = daysAgo === 0 ? 'Today' : daysAgo === 1 ? '1d ago' : daysAgo !== null && daysAgo <= 30 ? `${daysAgo}d ago` : null;
              return (
              <div key={job.id} onClick={() => navigate(`/jobs/${job.id}`)}
                className="job-card bg-white rounded-3xl border border-[#E5E0D8] p-6 cursor-pointer shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`job-card-${job.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[#F4F1E8] flex items-center justify-center">
                    <Briefcase size={18} className="text-[#047857]" strokeWidth={1.5} />
                  </div>
                  <div className="flex gap-1.5 flex-wrap justify-end">
                    {job.verified && <span className="tag-pill text-[10px] bg-[#A7F3D0]/30 text-[#047857]"><CheckCircle2 size={10} className="mr-0.5" />Verified</span>}
                    {job.featured && <span className="tag-pill text-[10px] bg-purple-100 text-purple-700"><Star size={10} className="mr-0.5" />Featured</span>}
                    {job.equipment_provided && <span className="tag-pill text-[10px]"><Monitor size={10} className="mr-1" strokeWidth={1.5} />Equipment Provided</span>}
                    {job.quick_apply && <span className="tag-pill text-[10px]"><Zap size={10} className="mr-1" strokeWidth={1.5} />Quick Apply</span>}
                  </div>
                </div>
                <h3 className="font-medium text-base mb-1 line-clamp-1" style={{fontFamily:'Outfit'}}>{job.title}</h3>
                <p className="text-sm text-[#1C2A24]/60 mb-1">{job.company}</p>
                {job.category && <p className="text-[11px] text-[#047857]/70 mb-2 capitalize">{job.category.replace(/-/g, ' ')}</p>}
                <div className="flex items-center gap-3 text-xs text-[#1C2A24]/50 mb-3 flex-wrap">
                  <span className="flex items-center gap-1"><MapPin size={12} strokeWidth={1.5} />Remote — {job.state}</span>
                  <span>{job.job_type}</span>
                  {postedLabel && <span className="flex items-center gap-1"><Clock size={11} strokeWidth={1.5} />{postedLabel}</span>}
                </div>
                {pay.isDisclosed
                  ? <p className="text-sm font-medium text-[#047857]" data-testid="job-pay-disclosed">{pay.text}</p>
                  : <p className="text-xs text-[#1C2A24]/40 flex items-center gap-1" data-testid="job-pay-undisclosed"><EyeOff size={11} strokeWidth={1.5} />{pay.text}</p>
                }
                <p className="text-xs text-[#1C2A24]/40 mt-2">{job.applicants || 0} applicants</p>
              </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-8" data-testid="pagination">
            <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft size={16} strokeWidth={1.5} />
            </Button>
            <span className="text-sm text-[#1C2A24]/60">Page {page} of {pages}</span>
            <Button variant="outline" size="sm" className="rounded-full" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight size={16} strokeWidth={1.5} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
